import type { BudgetCeiling, BudgetReserve, ContactDto, OrgDto, ProjectDto, SitePlace } from '@nsb/core';
import { changedLabels, sameValue, type FormField } from '../unsaved.ts';

/**
 * What each form with a Save button has not saved, by the labels on screen, for the one bar at the window's foot
 * (P-29; design study 28, A; src/unsaved.ts). Each compares the fields as the form shows them with what was loaded, as
 * the save would keep them, so the bar goes once they are saved. Pure, so the web tests hold them; the smaller forms
 * name theirs where they are, through the same `changedLabels`.
 */

/** A project's Settings (screens/project/ProjectSettings.tsx). */
const SETTINGS_FIELDS: readonly FormField<ProjectDto>[] = [
  ['Name', (p) => p.name],
  ['Project code', (p) => p.code],
  ['Site address', (p) => [p.siteAddress, p.sitePlace]],
  ['Jurisdiction', (p) => p.jurisdiction],
  ['Source', (p) => p.sourceNote],
  ['Goal', (p) => p.summary],
  ['Fee override %', (p) => p.feePctOverride],
  ['Contingency override %', (p) => p.contingencyPctOverride],
  ['What the client sees', (p) => p.clientShows],
];

export const settingsChanges = (was: ProjectDto, now: ProjectDto) => changedLabels(was, now, SETTINGS_FIELDS);

/** A person on People as the form holds them. */
export interface PersonRow {
  id?: string;
  name: string;
  email: string;
  phone: string;
  isPrimary: boolean;
}

/**
 * A project's People (screens/project/People.tsx): whose name, email or phone changed (the address as it is kept,
 * trimmed and in lower case), who is primary now, and who is added. A new row left blank is no change: saving drops it.
 */
export function peopleChanges(contacts: readonly ContactDto[], rows: readonly PersonRow[]): string[] {
  const byId = new Map(contacts.map((c) => [c.id, c]));
  const names: string[] = [];
  for (const r of rows) {
    const c = r.id ? byId.get(r.id) : undefined;
    if (!c) {
      if (r.name.trim()) names.push(`${r.name.trim()}, added`);
      continue;
    }
    if (!sameValue(r.name, c.name)) names.push(`${c.name}’s name`);
    if (!sameValue(r.email.toLowerCase(), c.email)) names.push(`${c.name}’s email`);
    if (!sameValue(r.phone, c.phone)) names.push(`${c.name}’s phone`);
    if (r.isPrimary && !c.isPrimary) names.push(`Primary: ${r.name.trim() || c.name}`);
  }
  return names;
}

/** The organization's settings as the form holds them (screens/settings/Settings.tsx). */
export interface OrgFields {
  name: string;
  fee: number | null;
  cont: number | null;
  where: { text: string; place: SitePlace | null };
}

export const orgFields = (org: OrgDto): OrgFields => ({ name: org.name, fee: org.feePct, cont: org.contingencyPct, where: { text: org.placeAddress ?? '', place: org.place } });

export const orgChanges = (org: OrgDto, now: OrgFields) =>
  changedLabels(orgFields(org), now, [
    ['Organization', (x) => x.name],
    ['Where NSB is', (x) => x.where],
    ['Fee %', (x) => x.fee],
    ['Contingency %', (x) => x.cont],
  ]);

/** A budget being proposed, as the form holds it (screens/project/Budget.tsx). */
export interface ProposalFields {
  amount: number | null;
  reserve: BudgetReserve;
  note: string;
}

/** What a budget is proposed from: the budget as it stands, its amount as the field shows it, or blank for the first. */
export const proposalStart = (c: BudgetCeiling | null): ProposalFields => ({ amount: c ? (c.reserve === 'on_top' ? c.workCents : c.totalCents) : null, reserve: c?.reserve ?? 'inside', note: '' });

/** What a budget being proposed has changed from the budget as it stands; its note is asked only for a change. */
export function proposalChanges(c: BudgetCeiling | null, now: ProposalFields, { pct, staff }: { pct: number; staff: boolean }): string[] {
  const fields: FormField<ProposalFields>[] = [['The budget', (x) => x.amount]];
  if (pct > 0) fields.push([`The ${pct}% contingency`, (x) => x.reserve]);
  if (c) fields.push([staff ? 'Why' : 'What changed', (x) => x.note]);
  return changedLabels(proposalStart(c), now, fields);
}
